import { FC, useState } from "react";

import { CourseEnrollmentTable } from "./SectionEnrollmentTable";

import { serverGetCourseEnrollmentSummary } from "api/api-server-data";
import { CourseCodeMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { ArrowDownIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectTerms } from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";
import { toastErrorMessage } from "utils/alerts";
import { downloadCSVFile } from "utils/file";
import { generateCourseEnrollmentCSV } from "utils/reporting";

export const CourseEnrollmentReport: FC = () => {
  const [selectedCourseCodeId, setSelectedCourseCodeId] = useState("");
  const [selectedTermId, setSelectedTermId] = useState("");
  const terms = useAppSelector(selectTerms);

  const { data, isLoading, error } = usePromiseEffect(async () => {
    if (!selectedTermId) {
      return [];
    }

    return serverGetCourseEnrollmentSummary({
      term_id: selectedTermId,
      course_id: selectedCourseCodeId || undefined,
    });
  }, [selectedTermId, selectedCourseCodeId]);

  const handleDownloadReport = () => {
    if (!selectedTermId) {
      toastErrorMessage(t("course_enrollment_report.select_term_to_view_data"));
      return;
    }

    if (!data || data.length === 0) {
      toastErrorMessage(t("course_enrollment_report.no_data"));
      return;
    }

    try {
      const selectedTerm = terms.find((term) => term.id === selectedTermId);
      const termTitle = selectedTerm?.title || selectedTermId;

      const { csvData, filename } = generateCourseEnrollmentCSV(data, termTitle);
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("course_enrollment_report.export_failed"));
    }
  };

  return (
    <div className="course-enrollment-view">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="h3">{t("course_enrollment_report.title")}</h2>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <TermMenu
            defaultToAll={false}
            eventPrefix="Home - Reports / "
            menuType="menu"
            selectedTermId={selectedTermId}
            size="s"
            onTermChange={setSelectedTermId}
          />
        </div>

        <div className="flex items-center gap-2">
          <CourseCodeMenu
            menuType="menu"
            selectedCourseCodeId={selectedCourseCodeId}
            size="s"
            onCourseCodeChange={setSelectedCourseCodeId}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            disabled={!selectedTermId || !data || data.length === 0}
            eventName="Home - Reports / Download course enrollment"
            kind="white"
            size="s"
            onClick={handleDownloadReport}
          >
            <IconText iconStart={<ArrowDownIcon />} text={t("course_enrollment_report.download")} />
          </Button>
        </div>
      </div>

      {/* Error message */}
      {error && <div className="bg-red-50 text-red-700 mb-4 rounded-md p-4">{error.message}</div>}

      {/* Loading state */}
      {isLoading && (
        <div className="mt-8 text-center text-black-tint-40">{t("common.loading")}...</div>
      )}

      {/* Table */}
      {data && <CourseEnrollmentTable data={data} />}

      {/* No data message */}
      {data && !error && data.length === 0 && (
        <div className="mt-8 text-center text-black-tint-40">
          {selectedTermId
            ? t("course_enrollment_report.no_data")
            : t("course_enrollment_report.select_term_to_view_data")}
        </div>
      )}
    </div>
  );
};
